'use client';

import { PageTitleNavigationProvider } from '@umodeapporg/ui/components/Navigation/PageTitleNavigationContext';
import AppShell from '@umodeapporg/ui/components/Shell/AppShell';
import ShellLanguageToggle from '@umodeapporg/ui/components/Shell/LanguageToggle';
import ShellThemeToggle from '@umodeapporg/ui/components/Shell/ThemeToggle';

import { AgenteFloatingButton, useAgenteFloatingButtonVisivel } from '@/components/agente/AgenteFloatingButton';
import { AgentePainel } from '@/components/agente/AgentePainel';
import { useTheme } from '@/components/theme/ThemeProvider';
import { useAgenteBootstrap } from '@/domain/agente/hooks/useAgenteBootstrap';
import { useLanguage } from '@/domain/common/hooks/useLanguage';
import { useConfiguracaoBootstrap } from '@/domain/configuracao/hooks/useConfiguracaoBootstrap';
import { usePermissions, usePermissionsBootstrap } from '@/domain/permissions/hooks/usePermissions';
import { PORTAL_PATH } from '@/domain/portal/helpers/sessionAudience';
import { usePortalAcesso, usePortalAcessoBootstrap } from '@/domain/portal/hooks/usePortalAcesso';
import { logout } from '@/infrastructure/http/authRefresh';
import { cn } from '@/lib/utils';

import { AcessoDaRota } from './AcessoDaRota';
import { DocumentTitle } from './DocumentTitle';
import { FilaTrigger } from './FilaTrigger';
import { useFooterItems, useNavigationItems, usePageTitleTrailResolver } from './navigation';
import { ShellBrand } from './ShellBrand';
import { UserMenu } from './UserMenu';

/**
 * Room for the assistant launcher (fixed `bottom-5`, `size-14`: 76px from the bottom edge) at
 * the end of EVERY page, applied once to the shell's scroll container instead of page by page.
 * The page frame already ends in 24-32px of padding; this adds 56px on top of it, so the last
 * row, button or footer of any page can scroll above the button. The container is the
 * library's `#scrollableDiv`, which takes no class of its own, hence the descendant variant on a
 * box-less wrapper.
 */
const RESERVA_DO_AGENTE = '[&_#scrollableDiv]:pb-14';

const HOME_PATH = '/';

interface DashboardLayoutProps {
  children: React.ReactNode;
}

/**
 * The CosturAI chrome is the library shell: menu items, header tools and the account
 * surface arrive as props. The agent is the home AND a side panel on every other screen,
 * so its conversation is loaded here, once, and shared by both.
 */
export function DashboardLayout({ children }: DashboardLayoutProps) {
  const { preference, setPreference } = useTheme();
  const { currentLanguage, changeLanguage } = useLanguage();
  // Who is signed in comes first: a brand buyer only reaches the Portal, so the menu, the
  // routes and every other shell load wait for this answer.
  usePortalAcessoBootstrap();
  // What the role allows in this account: the menu, the route gate and the write controls read it.
  usePermissionsBootstrap();
  useAgenteBootstrap();
  // The factory parameters are session data: plain helpers read them on every screen.
  useConfiguracaoBootstrap();
  const { isBrandBuyer } = usePortalAcesso();
  const { canViewRoute } = usePermissions();

  const items = useNavigationItems();
  const footerItems = useFooterItems();
  // The foot of the sidebar is still the menu: PageTitle reads its icon and breadcrumb from
  // the same list, so /ajuda keeps both.
  const resolveTrail = usePageTitleTrailResolver([...items, ...footerItems]);
  const reservaDoAgente = useAgenteFloatingButtonVisivel();

  return (
    <>
      <DocumentTitle />
      {/* `contents`: the wrapper draws no box, so the shell lays out exactly as before. */}
      <div className={cn('contents', reservaDoAgente && RESERVA_DO_AGENTE)}>
        <AppShell
          items={items}
          footerItems={footerItems}
          // The brand mark leads a buyer to the one screen they reach, and staff whose role
          // has no Início to the first screen it does open.
          homePath={isBrandBuyer ? PORTAL_PATH : canViewRoute(HOME_PATH) ? HOME_PATH : (items[0]?.to ?? HOME_PATH)}
          logo={<ShellBrand />}
          toolsSlot={<FilaTrigger />}
          controlsSlot={
            <>
              <ShellThemeToggle preference={preference} onChange={setPreference} />
              <ShellLanguageToggle language={currentLanguage} onChange={changeLanguage} />
            </>
          }
          userMenuSlot={<UserMenu />}
          onLogout={logout}
        >
          <PageTitleNavigationProvider resolveTrail={resolveTrail}>
            <AcessoDaRota>{children}</AcessoDaRota>
          </PageTitleNavigationProvider>
        </AppShell>
      </div>
      <AgenteFloatingButton />
      <AgentePainel />
    </>
  );
}
